import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from './supabase';
import { Card, CardTitle, Stat } from './ui/Card';
import { Table as UITable, TableCard, THead, Th, Tr, Td, Pill, cn } from './ui/Table';
import { Plane, Plus, Trash2, Info } from 'lucide-react';
import { DHL_RATES, DHL_EXTRA_PER_KG, DHL_PREMIUM, DHL_DESTINOS, DHL_VOLUMETRIC_DIVISOR } from './dhlRates';

// Calculadora de envíos DHL Express (export desde Argentina), en /dhl.
// Cobra por el MAYOR entre peso real y volumétrico (L×An×Al/5000), redondeado
// hacia arriba al escalón del tarifario (0,5 kg hasta 20; de a 1 kg arriba de 20).

type Bulto = { id: number; largo: string; ancho: string; alto: string; peso: string; cant: string };

const nuevoBulto = (id: number): Bulto => ({ id, largo: '', ancho: '', alto: '', peso: '', cant: '1' });
const usd = (n: number) => 'US$ ' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const ars = (n: number) => '$' + Math.round(n).toLocaleString('es-AR');

// Sube el peso al escalón del tarifario que corresponde cobrar.
function escalon(peso: number): number {
    if (peso <= 0) return 0;
    for (const [kg] of DHL_RATES) if (kg >= peso - 1e-9) return kg;
    return Math.ceil(peso); // arriba de 300 kg
}

function tarifa(pesoCobrable: number, zona: number): number | null {
    if (pesoCobrable <= 0) return null;
    const i = zona - 1;
    const step = escalon(pesoCobrable);
    const hit = DHL_RATES.find(([kg]) => kg === step);
    if (hit) return hit[i + 1];
    // > 300 kg: tarifa de 300 + adicional por kg
    const base = DHL_RATES[DHL_RATES.length - 1];
    return base[i + 1] + (step - base[0]) * DHL_EXTRA_PER_KG[i];
}

export function Dhl() {
    const [bultos, setBultos] = useState<Bulto[]>([nuevoBulto(1)]);
    const [destino, setDestino] = useState('latam');
    const [premium, setPremium] = useState('none');
    const [combustible, setCombustible] = useState('30');
    const [usdRate, setUsdRate] = useState<number>(0);

    useEffect(() => {
        supabase.from('cost_settings').select('usd_rate').eq('id', 1).maybeSingle()
            .then(({ data }) => setUsdRate(Number(data?.usd_rate) || 0));
    }, []);

    const dest = DHL_DESTINOS.find(d => d.id === destino) || DHL_DESTINOS[0];
    const prem = DHL_PREMIUM.find(p => p.id === premium) || DHL_PREMIUM[0];

    const calc = useMemo(() => {
        const detalle = bultos.map(b => {
            const L = Number(b.largo) || 0, A = Number(b.ancho) || 0, H = Number(b.alto) || 0;
            const cant = Math.max(1, Number(b.cant) || 1);
            const real = (Number(b.peso) || 0) * cant;
            const vol = (L * A * H / DHL_VOLUMETRIC_DIVISOR) * cant;
            return { ...b, cant, real, vol, cobrable: Math.max(real, vol), usaVol: vol > real };
        });
        const real = detalle.reduce((s, d) => s + d.real, 0);
        const vol = detalle.reduce((s, d) => s + d.vol, 0);
        const cobrable = Math.max(real, vol);
        const step = escalon(cobrable);
        const base = tarifa(cobrable, dest.zona);
        if (base == null) return { detalle, real, vol, cobrable, step, base: null, premiumExtra: 0, fuel: 0, total: 0 };
        const premiumExtra = prem.extra * bultos.length ? prem.extra : 0; // el premium es por envío
        const sub = base + prem.extra;
        const fuel = sub * (Number(combustible) || 0) / 100;
        return { detalle, real, vol, cobrable, step, base, premiumExtra: prem.extra, fuel, total: sub + fuel };
    }, [bultos, dest, prem, combustible]);

    const inputCls = 'w-full rounded-lg border border-line bg-transparent px-2 py-1.5 text-[0.85rem] text-ink tabular-nums';
    const lbl = 'flex flex-col gap-1 text-[0.72rem] font-semibold text-faint';
    const upd = (id: number, patch: Partial<Bulto>) => setBultos(bs => bs.map(b => b.id === id ? { ...b, ...patch } : b));

    // comparativa de zonas para el mismo peso
    const comparativa = calc.base != null ? DHL_DESTINOS.map(d => ({ ...d, precio: tarifa(calc.cobrable, d.zona) })) : [];

    return (
        <div className="mx-auto flex max-w-[1000px] flex-col gap-4 p-4">
            <div className="flex flex-wrap items-center gap-3">
                <h3 className="m-0 flex items-center gap-2 text-[1.2rem] font-extrabold text-ink"><Plane size={20} /> Calculadora DHL Express</h3>
                <span className="text-[0.74rem] text-faint">Export desde Argentina · tarifario 2026 · precios en USD</span>
            </div>

            {/* Bultos */}
            <Card>
                <CardTitle right={
                    <button onClick={() => setBultos([...bultos, nuevoBulto(Date.now())])}
                        className="flex items-center gap-1 rounded-lg border border-line px-2.5 py-1 text-[0.78rem] text-soft">
                        <Plus size={13} /> Bulto
                    </button>
                }>Medidas y peso</CardTitle>
                <TableCard>
                    <UITable>
                        <THead><tr><Th>Largo (cm)</Th><Th>Ancho (cm)</Th><Th>Alto (cm)</Th><Th>Peso real (kg)</Th><Th>Cant.</Th><Th align="right">Volumétrico</Th><Th> </Th></tr></THead>
                        <tbody>
                            {calc.detalle.map(d => (
                                <Tr key={d.id}>
                                    <Td><input type="number" className={inputCls} value={d.largo} onChange={e => upd(d.id, { largo: e.target.value })} placeholder="0" /></Td>
                                    <Td><input type="number" className={inputCls} value={d.ancho} onChange={e => upd(d.id, { ancho: e.target.value })} placeholder="0" /></Td>
                                    <Td><input type="number" className={inputCls} value={d.alto} onChange={e => upd(d.id, { alto: e.target.value })} placeholder="0" /></Td>
                                    <Td><input type="number" step="0.1" className={inputCls} value={d.peso} onChange={e => upd(d.id, { peso: e.target.value })} placeholder="0" /></Td>
                                    <Td><input type="number" min={1} className={cn(inputCls, 'w-16')} value={d.cant} onChange={e => upd(d.id, { cant: e.target.value })} /></Td>
                                    <Td align="right" className="whitespace-nowrap">
                                        {d.vol > 0 ? (
                                            <span className={d.usaVol ? 'font-bold text-amber-600' : 'text-soft'}>
                                                {d.vol.toFixed(2)} kg{d.usaVol ? ' ⚠' : ''}
                                            </span>
                                        ) : <span className="text-faint">—</span>}
                                    </Td>
                                    <Td align="right">
                                        {bultos.length > 1 && (
                                            <button className="text-red-500" onClick={() => setBultos(bs => bs.filter(x => x.id !== d.id))}><Trash2 size={14} /></button>
                                        )}
                                    </Td>
                                </Tr>
                            ))}
                        </tbody>
                    </UITable>
                </TableCard>
                <p className="mb-0 mt-2 flex items-start gap-1.5 text-[0.7rem] leading-relaxed text-faint">
                    <Info size={13} className="mt-0.5 shrink-0" />
                    DHL cobra por el mayor entre el peso real y el volumétrico (largo × ancho × alto ÷ {DHL_VOLUMETRIC_DIVISOR}).
                    El ⚠ marca los bultos donde manda el volumen — ahí conviene achicar la caja.
                </p>
            </Card>

            {/* Destino + opciones */}
            <Card>
                <CardTitle>Destino y servicio</CardTitle>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                    <label className={lbl}>Destino
                        <select value={destino} onChange={e => setDestino(e.target.value)} className={inputCls}>
                            {DHL_DESTINOS.map(d => <option key={d.id} value={d.id}>{d.label} (zona {d.zona})</option>)}
                        </select>
                    </label>
                    <label className={lbl}>Servicio
                        <select value={premium} onChange={e => setPremium(e.target.value)} className={inputCls}>
                            {DHL_PREMIUM.map(p => <option key={p.id} value={p.id}>{p.label}{p.extra ? ` (+US$ ${p.extra})` : ''}</option>)}
                        </select>
                    </label>
                    <label className={lbl}>Recargo combustible %
                        <input type="number" className={inputCls} value={combustible} onChange={e => setCombustible(e.target.value)} />
                    </label>
                </div>
                <p className="mb-0 mt-2 text-[0.7rem] leading-relaxed text-faint">{dest.detalle}</p>
            </Card>

            {/* Resultado */}
            <Card className="border-2">
                <CardTitle right={usdRate > 0 ? <span className="text-[0.72rem] text-faint">USD ${usdRate.toLocaleString('es-AR')}</span> : null}>Cotización</CardTitle>
                {calc.base == null ? (
                    <div className="py-6 text-center text-[0.9rem] text-soft">Cargá las medidas y el peso para ver el precio.</div>
                ) : (
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-[1.2fr_1fr]">
                        <div className="text-[0.85rem]">
                            {[
                                ['Peso real total', `${calc.real.toFixed(2)} kg`],
                                ['Peso volumétrico total', `${calc.vol.toFixed(2)} kg`],
                                ['Peso a cobrar', `${calc.cobrable.toFixed(2)} kg → escalón ${calc.step} kg`],
                            ].map(([k, v], i) => (
                                <div key={i} className="flex items-baseline justify-between border-t border-line py-1">
                                    <span className={i === 2 ? 'font-bold' : 'text-soft'}>{k}</span>
                                    <span className={cn('tabular-nums', i === 2 && 'font-bold')}>{v}</span>
                                </div>
                            ))}
                            <div className="mt-2 flex items-baseline justify-between border-t border-line py-1">
                                <span>Tarifa {dest.label} (zona {dest.zona})</span>
                                <span className="tabular-nums">{usd(calc.base)}</span>
                            </div>
                            {calc.premiumExtra > 0 && (
                                <div className="flex items-baseline justify-between py-1 text-soft">
                                    <span>{prem.label}</span><span className="tabular-nums">{usd(calc.premiumExtra)}</span>
                                </div>
                            )}
                            {calc.fuel > 0 && (
                                <div className="flex items-baseline justify-between py-1 text-soft">
                                    <span>Recargo combustible ({combustible}%)</span><span className="tabular-nums">{usd(calc.fuel)}</span>
                                </div>
                            )}
                        </div>
                        <div className="flex flex-col gap-3">
                            <Stat label="Total estimado" value={usd(calc.total)}
                                sub={usdRate > 0 ? <>≈ <b>{ars(calc.total * usdRate)}</b> al cambio de config</> : undefined} />
                            {calc.detalle.length > 1 && (
                                <Stat label="Bultos" value={calc.detalle.reduce((s, d) => s + d.cant, 0)} sub="el peso se suma entre todos" />
                            )}
                        </div>
                    </div>
                )}
            </Card>

            {/* Comparativa por zona */}
            {calc.base != null && (
                <Card>
                    <CardTitle>Mismo envío a otros destinos ({calc.step} kg, sin premium ni combustible)</CardTitle>
                    <TableCard>
                        <UITable>
                            <THead><tr><Th>Destino</Th><Th>Zona</Th><Th align="right">Tarifa</Th><Th align="right">vs. elegido</Th></tr></THead>
                            <tbody>
                                {comparativa.map(c => (
                                    <Tr key={c.id} className={c.id === destino ? 'bg-[color-mix(in_srgb,var(--border)_25%,transparent)]' : undefined}>
                                        <Td className={c.id === destino ? 'font-bold' : ''}>{c.label}</Td>
                                        <Td><Pill tone="neutral">{c.zona}</Pill></Td>
                                        <Td align="right" className="font-semibold">{c.precio != null ? usd(c.precio) : '—'}</Td>
                                        <Td align="right" className="text-soft">
                                            {c.precio != null && calc.base != null && c.id !== destino
                                                ? (c.precio > calc.base ? '+' : '') + usd(c.precio - calc.base).replace('US$ ', '')
                                                : '—'}
                                        </Td>
                                    </Tr>
                                ))}
                            </tbody>
                        </UITable>
                    </TableCard>
                </Card>
            )}

            <p className="mb-8 text-[0.7rem] leading-relaxed text-faint">
                Tarifario oficial DHL Express Argentina, código ARBZ00009/TDE_ECOMM_03 (vigente desde 01-01-2026), servicio
                Worldwide Export. Los precios ya incluyen los adicionales del tarifario, pero <b>no</b> el recargo por
                combustible (variable, se carga arriba), ni cargos aduaneros, impuestos o liberación de carga en destino.
                Máximo por pieza: 300 cm de largo y 1.000 kg.
            </p>
        </div>
    );
}

export default Dhl;
